import { lazy } from 'react';
import _ from 'lodash';
import { Redirect } from 'react-router-dom';

import { isCCTVConnect, isDemo, isMobileApp } from '../utils/getHost';
import { isVideoloftDemoUid } from '../api/multiuser';

import translationKeys from '../translations/keys';
import { PARTNER_PORTAL_START_PATH } from './partner-portal/config';

// Ditch external redirects as soon as portal, web client, etc. have been incorporated into the app
import ExternalRedirect from '../components/external-redirect';

// Icons
import { ReactComponent as CamerasIcon } from '../assets/images/icons/cameras.svg';
import { ReactComponent as EventsIcon } from '../assets/images/icons/motion.svg';
import { ReactComponent as AlertsIcon } from '../assets/images/icons/alert.svg';
import { ReactComponent as SettingsIcon } from '../assets/images/icons/manage.svg';
import { ReactComponent as ManageIcon } from '../assets/images/icons/manage2.svg';
import { ReactComponent as PlaybackIcon } from '../assets/images/icons/playback.svg';
import { ReactComponent as DashboardIcon } from '../assets/images/icons/dashboard.svg';
import { ReactComponent as VehicleIcon } from '../assets/images/icons/car.svg';
import { ReactComponent as AccountIcon } from '../assets/images/icons/account.svg';
import { ReactComponent as OrdersIcon } from '../assets/images/icons/orders.svg';
import { ReactComponent as GraphIcon } from '../assets/images/icons/graph.svg';
import { ReactComponent as PartnerPortalIcon } from '../assets/images/icons/partner-portal.svg';
import { ReactComponent as WhatsNewIcon } from '../assets/images/icons/whats-new.svg';
import { ReactComponent as EditIcon } from '../assets/images/icons/edit.svg';
import { ReactComponent as ResetIcon } from '../assets/images/icons/reset.svg';
import { ReactComponent as InstallIcon } from '../assets/images/icons/install.svg';
import { ReactComponent as PPEIcon } from '../assets/images/icons/hivis.svg';
import { ReactComponent as AuditIcon } from '../assets/images/icons/audit.svg';
import ToolsIcon from '../assets/images/icons/tools';
import { ANALYTICS_TYPES } from '../utils/cameras';

const SignIn = lazy(() => import('./sign-in'));
const Register = lazy(() => import('./register'));
const ForgotPassword = lazy(() => import('./forgot-password'));
const ResetPassword = lazy(() => import('./reset-password'));
const PermissionLevels = lazy(() => import('./permission-levels'));
const AboutAuditLogs = lazy(() => import('./about-audit-logs'));
const AcceptInvite = lazy(() => import('./accept-invite'));
const GetStarted = lazy(() => import('./get-started'));
const Payment = lazy(() => import('./payment'));
const NewPricing = lazy(() => import('./new-pricing'));
const AppRegisterSuccess = lazy(() => import('./app-register-success'));
const PaypalConfirm = lazy(() => import('./paypal-confirm'));
const CloudAdapters = lazy(() => import('./cloud-adapters'));
const CameraStatusPage = lazy(() => import('./camera-status'));
const WebsiteDown = lazy(() => import('./website-down'));
const Customer = lazy(() => import('./customer'));
const Account = lazy(() => import('./account'));
const Cameras = lazy(() => import('./cameras'));
const Playback = lazy(() => import('./playback'));
const Events = lazy(() => import('./events'));
const Dashboard = lazy(() => import('./dashboard'));
const CameraSettings = lazy(() => import('./camera-settings'));
const CreateEditCameraGroup = lazy(() => import('./camera-groups'));
const OAuthStart = lazy(() => import('./oauth-start'));
const OAuthEnd = lazy(() => import('./oauth-end'));
const Terms = lazy(() => import('./terms'));
const FullWhiteLabelTerms = lazy(() => import('./terms/FullWhiteLabelTerms'));
const Privacy = lazy(() => import('./privacy'));
const FullWhiteLabelPrivacy = lazy(() => import('./privacy/FullWhiteLabelPrivacy'));
const Install = lazy(() => import('./install'));
const Support = lazy(() => import('./support'));
const Health = lazy(() => import('./health'));
const Vehicles = lazy(() => import('./vehicles'));
const AuditLogs = lazy(() => import('./audit-logs'));
const Analytics = lazy(() => import('./analytics-page'));
const Shared = lazy(() => import('./shared'));
const Alerts = lazy(() => import('./alerts'));
const PartnerPortal = lazy(() => import('./partner-portal'));

// Takeaways pages
const TakeawayOrders = lazy(() => import('./ordercam/takeaway-orders'));
const TakeawayDashboard = lazy(() => import('./ordercam/dashboard'));
const TakeawaySettings = lazy(() => import('./ordercam/takeaway-settings'));

const OrdercamSchemeManager = lazy(() => import('./support/ordercam/OrdercamSchemeManager'));

// Layout options
export const LAYOUT_FULLSCREEN = 'FULLSCREEN'; // Narrow width, logo, background
export const LAYOUT_NONE = 'NONE'; // Blank canvas
export const LAYOUT_HEADER_FOOTER = 'HEADER_FOOTER'; // Header and footer
export const LAYOUT_APP = 'LAYOUT_APP'; // Sidebar
const AUDIT_LOGS_ALLOWED_APP_NAMES = [
    'videoloft',
    'cctv connect',
    'epcom cloud',
    'videosafes',
    '900vms',
    'kirz cctv',
    'st live',
    'scati cloud',
    'jobcam'
];

/*
Each route can have the following options:

- path (string, required): relative URL path to page
- renderComponent (function, required if externalRedirect is not true): function called by App to create page's component. This function gets passed accountData, an object containing functions for manipulating user account data stored in App.jsx, and a boolean indicating whether web app is being accessed through mobile app
- translationKey (string, optional): translation key for the name of this page
- navbar (boolean, optional): include this page in the navbar (header version)
- sidebarNav (boolean, optional): include this page in the 'App layout' sidebar (in the top section)
- sidebarNavSecondary (boolean, optional): include this page in the lower section of the 'App layout' sidebar
- startPath (string, optional): preferred landing URL for navigation to this route (for example, include default query params)
- mobileApp (boolean, optional): Whether to include this page in the mobile app. Currently only filters what appears in navigation bar but may completely exclude pages appearing in future.
- icon (SVG ReactComponent, optional): if `sidebarNav` is true, this is used as the icon
- restrictAccess (array, optional): includes one or more of 'u', 'i', 'd' and 's' depending on which account types can access the page. Exclude this option if page is public
- redirectIfLoggedIn (boolean, optional): if true, page can only be accessed if not logged in (e.g. sign in page)
- layout (string or object, optional): determines layout of page (either with header and footer; sidebar; logo, narrow width; or nothing at all)
    object is structured:
        {
            type: string, one of ['FULLSCREEN', 'NONE', 'LAYOUT_APP', 'HEADER_FOOTER' (default)], optional
            narrow: boolean, only applies to HEADER_FOOTER layouts, sets max width on page
            fullWidth: boolean, only applies to LAYOUT_APP layouts, removes padding from <main>
        }
    if only needing to specify 'type' string, then this can be set as 'layout' rather than an object
- routes (object, optional): subroutes of this route. Object is structured identically to the overall routes object: each key is page name and value is an object with all the same possible properties as in this list
- whiteLabelCondition (function, optional): function which takes white label info as its only argument and returns a boolean. Called once white label info has been fetched. If function returns false, page is deleted from routes (i.e. page is not to appear for this white label). Only uses host info, not installer branding too if on CCTVC
- accountCondition (function, optional): function which takes logged in user's account info as its only argument and returns a boolean indicating whether page can be accessed
- demo (boolean, optional): Whether to include page in white label demo. If true, page must use LAYOUT_APP
- beta (boolean, optional): Show "BETA" next to navlink in sidebar
*/
export const routes = {
    ordercamSchemes: {
        path: '/support/ordercamschemes',
        layout: LAYOUT_APP,
        restrictAccess: ['s'],
        renderComponent: () => (
            <OrdercamSchemeManager />
        )
    },

    support: {
        path: '/support',
        layout: LAYOUT_APP,
        renderComponent: () => (
            <Support />
        ),
        restrictAccess: ['s'],
        sidebarNav: true,
        icon: ToolsIcon,
        translationKey: 'Support',
        routes: {
            oldUser: {
                path: '/:uid',
            },
            users: {
                path: '/users',
                routes: {
                    user: {
                        path: '/:uid'
                    }
                }
            },
            installers: {
                path: '/installers',
                routes: {
                    installer: {
                        path: '/:installerId'
                    }
                }
            },
            tools: {
                path: '/tools'
            }
        },
        // Only show for VL (not that it matters otherwise as no one else will have staff logins)
        whiteLabelCondition: ({ isVideoloft }) => isVideoloft
    },    
    shared: {
        path: '/shared/:token',
        layout: LAYOUT_HEADER_FOOTER,
        renderComponent: (accountData) => <Shared isSignedIn={!_.isNil(accountData.uid)}/>
    },
    health: {
        path: '/health',
        layout: LAYOUT_APP,
        renderComponent: () => (
            <Health />
        ),
        restrictAccess: ['s'],
        sidebarNav: true,
        icon: DashboardIcon,
        translationKey: 'Health',
        // Only show for VL (not that it matters otherwise as no one else will have staff logins)
        whiteLabelCondition: ({ isVideoloft }) => isVideoloft
    },

    multicam: {
        // Got this in case I've missed any links to old multicam
        path: '/multicam',
        layout: LAYOUT_NONE,
        renderComponent: () => <Redirect to={"/cameras"} />
    },
    myCameras: {
        translationKey: translationKeys.navigation.MY_CAMERAS,
        path: '/viewer',
        restrictAccess: ['u', 'i', 'd', 's'],
        renderComponent: () => <ExternalRedirect to="/viewer" />,
        layout: LAYOUT_NONE,
    },
    signIn: {
        path: '/sign-in',
        acceptPaths: ['/sign-in.html'],
        layout: LAYOUT_FULLSCREEN,
        redirectIfLoggedIn: true,
        renderComponent: (
            accountData,
            { handleLogin, handleLogout, handleNetworkError }
        ) => (
            <SignIn
                updateAccountData={handleLogin}
                onInvalidToken={handleLogout}
                onNetworkError={handleNetworkError}
            />
        ),
        mobileApp: true
    },
    register: {
        path: '/register',
        layout: LAYOUT_FULLSCREEN,
        redirectIfLoggedIn: true,
        renderComponent: (accountData, { handleLogin, handleLogout, handleNetworkError, }) => (
            <Register updateAccountData={handleLogin} onInvalidToken={handleLogout} onNetworkError={handleNetworkError} />
        ),
    },
    forgotPassword: {
        path: '/forgot-password',
        acceptPaths: ['/forgotten-password.html'],
        layout: LAYOUT_FULLSCREEN,
        renderComponent: () => (
            <ForgotPassword />
        ),
        mobileApp: true
    },
    resetPassword: {
        path: '/reset-password',
        acceptPaths: ['/reset-password.html'],
        layout: LAYOUT_FULLSCREEN,
        renderComponent: () => (
            <ResetPassword />
        ),
        mobileApp: true
    },
    permissionLevels: {
        path: '/permission-levels',
        layout: {
            type: LAYOUT_HEADER_FOOTER,
            narrow: !isMobileApp,
        },
        renderComponent: () => <PermissionLevels />,
        mobileApp: true
    },
    aboutAuditLogs: {
        path: '/about-audit-logs',
        layout: {
            type: LAYOUT_HEADER_FOOTER,
            narrow: !isMobileApp,
        },
        renderComponent: () => <AboutAuditLogs />,
        mobileApp: true,
        demo: true
    },
    acceptInvite: {
        path: '/acceptinvite',
        layout: LAYOUT_FULLSCREEN,
        renderComponent: (
            { email },
            { handleLogout, handleNewOrganisation }
        ) => (
            <AcceptInvite
                email={email}
                onLogout={handleLogout}
                addOrganisation={handleNewOrganisation}
            />
        ),
    },
    getStarted: {
        path: '/get-started',
        acceptPaths: ['/quote/:id'],
        layout: LAYOUT_NONE,
        whiteLabelCondition: ({ canRegisterUsers }) => canRegisterUsers,
        renderComponent: (
            { uid, email, ssoProvider, isSelfReg, incompleteSelfRegSteps },
            { handleLogin, handleLogout, handleNetworkError, handleCompleteSelfRegStep, logoutAction },
        ) => (
            <GetStarted
                isSignedIn={!!uid}
                email={email}
                ssoProvider={ssoProvider}
                isSelfReg={isSelfReg}
                incompleteSelfRegSteps={incompleteSelfRegSteps}
                updateAccountData={handleLogin}
                onInvalidToken={handleLogout}
                onNetworkError={handleNetworkError}
                onSelfRegStepCompleted={handleCompleteSelfRegStep}
                onLogoutClick={logoutAction}
            />
        ),
        mobileApp: true
    },
    payment: {
        path: '/payment',
        layout: LAYOUT_FULLSCREEN,
        renderComponent: () => <Payment />,
    },
    newPricing: {
        path: '/new-pricing',
        renderComponent: ({ accountType }) => <NewPricing accountType={accountType} />,
        restrictAccess: ['u', 'i', 'd'],
        layout: LAYOUT_APP
    },
    appRegisterSuccess: {
        path: '/app-register-success',
        layout: LAYOUT_NONE,
        renderComponent: () => <AppRegisterSuccess />,
    },
    paypalConfirm: {
        path: '/paypal-confirm',
        layout: LAYOUT_NONE,
        renderComponent: () => (
            <PaypalConfirm />
        ),
        mobileApp: true
    },
    cloudAdapters: {
        path: '/cloud-adapters',
        acceptPaths: ['/cloud-adapter.html'],
        whiteLabelCondition: ({ canPurchaseHardware }) => canPurchaseHardware,
        renderComponent: ({ accountType }) => (
            <CloudAdapters
                isSignedIn={!!accountType}
                isEndUser={accountType === 'u'}
            />
        ),
    },
    cameraStatus: {
        path: '/camera-status',
        renderComponent: () => (
            <CameraStatusPage />
        ),
        demo: true,
        mobileApp: true
    },
    websiteDown: {
        path: '/backup',
        layout: LAYOUT_NONE,
        whiteLabelCondition: ({ appName }) => appName === 'Videoloft',
        renderComponent: ({ email }) => (
            <WebsiteDown isSignedIn={!!email} />
        ),
    },
    addCustomer: {
        path: '/customers/new',
        restrictAccess: ['i'],
        layout: {
            type: LAYOUT_NONE,
        },
        renderComponent: () => (
            <Customer />
        ),
    },
    editCustomer: {
        path: '/customers/:customerUid',
        restrictAccess: ['i'],
        layout: {
            type: LAYOUT_NONE,
        },
        renderComponent: () => (
            <Customer />
        ),
    },

    cameras: {
        path: '/cameras',
        translationKey: isMobileApp ? translationKeys.cameras.CAMERAS : translationKeys.cameras.status.LIVE,
        tooltipDescriptionKey: translationKeys.navigation.sidebar.tooltips.LIVE,
        navbar: true,
        sidebarNav: true,
        layout: {
            type: LAYOUT_APP
        },
        restrictAccess: ['u', 'i', 'd', 's'],
        icon: CamerasIcon,
        renderComponent: ({
            uid,
            organisations,
            cameraGroups,
            installAllowed
        }) => (
            <Cameras
                uid={uid}
                organisations={organisations}
                cameraGroups={cameraGroups}
                installAllowed={installAllowed}
            />
        ),
        routes: {
            camera: {
                path: '/:uidd',
            },
            grid: {
                path: '/grid'
            },
            layouts: {
                path: '/layouts',
                routes: {
                    layout: {
                        path: '/:id'
                    }
                }
            },
            presentationMode: {
                path: '/presentations',
                routes: {
                    presentation: {
                        path: '/:id'
                    }
                }
            }
        },
        whiteLabelCondition: ({ isTakeawayVendor }) => !isTakeawayVendor,
        demo: true,
        mobileApp: true
    },
    events: {
        path: '/events',
        translationKey: translationKeys.events.EVENTS,
        tooltipDescriptionKey: translationKeys.navigation.sidebar.tooltips.EVENTS,
        layout: LAYOUT_APP,
        navbar: true,
        sidebarNav: true,
        restrictAccess: ['u', 'i', 'd', 's'],
        icon: EventsIcon,
        renderComponent: ({
            uid,
            organisations,
            cameraGroups,
            installAllowed
        }) => (
            <Events
                uid={uid}
                organisations={organisations}
                cameraGroups={cameraGroups}
                installAllowed={installAllowed}
            />
        ),
        whiteLabelCondition: ({ isTakeawayVendor }) => !isTakeawayVendor,
        demo: true
    },
    alerts: {
        path: '/alerts',
        translationKey: isMobileApp ? translationKeys.alerts.ALERTS : translationKeys.alerts.SMART_MOTION_ALERTS,
        tooltipDescriptionKey: translationKeys.navigation.sidebar.tooltips.ALERTS,
        layout: LAYOUT_APP,
        navbar: true,
        sidebarNav: true,
        restrictAccess: ['u', 'i', 'd', 's'],
        icon: AlertsIcon,
        renderComponent: ({
            uid,
            organisations,
            cameraGroups,
            installAllowed
        }) => (
            <Alerts
                organisations={organisations}
                cameraGroups={cameraGroups}
                installAllowed={installAllowed}
            />
        ),
        whiteLabelCondition: ({ isTakeawayVendor, isVideoloft }) =>
            !isTakeawayVendor &&
            (!isDemo || isVideoloft || isCCTVConnect),
        demo: true,
        mobileApp: true
    },
    playback: {
        path: '/playback',
        translationKey: translationKeys.playback.PLAYBACK,
        tooltipDescriptionKey: translationKeys.navigation.sidebar.tooltips.PLAYBACK,
        layout: {
            type: LAYOUT_APP
        },
        navbar: true,
        sidebarNav: true,
        restrictAccess: ['u', 'i', 'd', 's'],
        icon: PlaybackIcon,
        renderComponent: ({ organisations, cameraGroups, installAllowed, uid }) => (
            <Playback
                loggedInUserUid={uid}
                organisations={organisations}
                cameraGroups={cameraGroups}
                installAllowed={installAllowed}
            />
        ),
        // Hide for Manything (to keep down costs) and receipts versions
        whiteLabelCondition: ({ isTakeawayVendor, isManything }) => !isTakeawayVendor && !isManything,
        demo: true,
    },
    dashboard: {
        path: '/dashboard',
        translationKey: translationKeys.navigation.DASHBOARD,
        tooltipDescriptionKey: translationKeys.navigation.sidebar.tooltips.DASHBOARD,
        layout: {
            type: LAYOUT_APP
        },
        navbar: true,
        sidebarNav: true,
        restrictAccess: ['u', 'i', 'd', 's'],
        icon: DashboardIcon,
        renderComponent: ({ uid, cameraGroups, organisations, installAllowed }, { handleNewCameraGroup }) => 
            <Dashboard 
                uid={uid} 
                cameraGroups={cameraGroups} 
                addCameraGroup={handleNewCameraGroup} 
                organisations={organisations} 
                installAllowed={installAllowed}
            />,
        whiteLabelCondition: ({ isTakeawayVendor, isManything }) => !isTakeawayVendor && !isManything,
        demo: true,
        mobileApp: true
    },
    settings: {
        path: '/settings',
        translationKey: translationKeys.technical.settings.SETTINGS,
        tooltipDescriptionKey: translationKeys.navigation.sidebar.tooltips.SETTINGS,
        layout: {
            type: LAYOUT_APP
        },
        navbar: true,
        sidebarNav: !isMobileApp,
        restrictAccess: ['u', 'i', 'd', 's'],
        icon: ToolsIcon,
        renderComponent: ({ uid, cameraGroups, organisations, installAllowed }) => <CameraSettings uid={uid} cameraGroups={cameraGroups} organisations={organisations} installAllowed={installAllowed} />,
        whiteLabelCondition: ({ isTakeawayVendor, isManything }) => !isTakeawayVendor && !isManything,
        demo: true,
        mobileApp: true
    },
    vehicles: {
        path: '/vehicles',
        layout: LAYOUT_APP,
        renderComponent: ({ organisations, cameraGroups, uid }) => (
            <Vehicles
                organisations={organisations}
                cameraGroups={cameraGroups}
                loggedInUserUid={uid}
            />
        ),
        restrictAccess: ['u', 'i', 'd', 's'],
        routes: {
            counts: {
                path: '/counts',
            },
            plate: {
                path: '/:plate'
            }
        },
        // Shows in main nav normally, but included in more menu in mobile app
        sidebarNav: !isMobileApp,
        sidebarNavSecondary: isMobileApp,
        icon: VehicleIcon,
        translationKey: translationKeys.vehicles.LPR,
        demo: true,
        whiteLabelCondition: ({ isVideoloft, disableVehiclesRoute, vehiclesDemo, isTakeawayVendor, isManything }) => {   
            const isCCTVConnectDemo = isCCTVConnect && window.location.pathname.match(/^\/demo(\/|$)/);
            return (
                !isTakeawayVendor &&
                !isManything &&
                // Don't show /vehicles page for white labels that don't want it or on demos that aren't setup (need to have vehiclesDemo: true)
                (isVideoloft || isCCTVConnect || !window.location.pathname.match(/^\/demo(\/|$)/) || vehiclesDemo) &&
                (isCCTVConnectDemo || !disableVehiclesRoute)
            );
        },
        mobileApp: true,
        group: 'analytics'
    },
    people: {
        path: '/people',
        layout: LAYOUT_APP,
        renderComponent: ({ organisations, cameraGroups, uid }) => (
            <Analytics
                type={ANALYTICS_TYPES.PEOPLE}
                organisations={organisations}
                cameraGroups={cameraGroups}
                loggedInUserUid={uid}
            />
        ),
        restrictAccess: ['u', 'i', 'd', 's'],
        // Shows in main nav normally, but included in more menu in mobile app
        sidebarNav: !isMobileApp,
        sidebarNavSecondary: isMobileApp,
        icon: PartnerPortalIcon,
        translationKey: translationKeys.peopleCounting.PEOPLE_COUNTING,
        demo: true,
        whiteLabelCondition: ({ isVideoloft, disablePeopleRoute, peopleDemo, isTakeawayVendor, isManything }) => {
            const isCCTVConnectDemo = isCCTVConnect && window.location.pathname.match(/^\/demo(\/|$)/);
            return (
                !isTakeawayVendor &&
                !isManything &&
                // Don't show /people page for white labels that don't want it or on demos that aren't setup,
                // but allow CCTV Connect demo to expose this route.
                (isVideoloft || isCCTVConnect || !window.location.pathname.match(/^\/demo(\/|$)/) || peopleDemo) &&
                (isCCTVConnectDemo || !disablePeopleRoute)
            );
        },
        mobileApp: true,
        group: 'analytics'
    },
    ppeDashboard: {
        path: '/ppe',
        layout: LAYOUT_APP,
        renderComponent: ({ organisations, cameraGroups, uid }) => (
            <Analytics
                type={ANALYTICS_TYPES.PPE}
                organisations={organisations}
                cameraGroups={cameraGroups}
                loggedInUserUid={uid}
            />
        ),
        restrictAccess: ['u', 'i', 'd', 's'],
        // Shows in main nav normally, but included in more menu in mobile app
        sidebarNav: !isMobileApp,
        sidebarNavSecondary: isMobileApp,
        icon: PPEIcon,
        translationKey: translationKeys.ppe.PPE,
        demo: true,
        whiteLabelCondition: ({ isVideoloft, disablePPERoute, ppeDemo, isTakeawayVendor, isManything }) => {
            const isCCTVConnectDemo = isCCTVConnect && window.location.pathname.match(/^\/demo(\/|$)/);
            return (
                !isTakeawayVendor &&
                !isManything &&
                // Don't show /ppe page for white labels that don't want it or on demos that aren't setup,
                // but allow CCTV Connect demo to expose this route.
                (isVideoloft || isCCTVConnect || !window.location.pathname.match(/^\/demo(\/|$)/) || ppeDemo) &&
                (isCCTVConnectDemo || !disablePPERoute)
            );
        },
        mobileApp: true,
        group: 'analytics'
    },
    auditLogs: {
        path: '/audit-logs',
        layout: LAYOUT_APP,
        tooltipDescriptionKey: translationKeys.navigation.sidebar.tooltips.AUDIT_LOGS,
        renderComponent: ({ organisations, cameraGroups, uid }) => (
            <AuditLogs
                organisations={organisations}
                cameraGroups={cameraGroups}
                loggedInUserUid={uid}
            />
        ),
        restrictAccess: ['u', 'i', 'd', 's'],
        // Page only available if user has cameras themselves or co-owns another account with cameras (or is a VL demo user)
        accountCondition: ({ organisations, uid }) =>
            isVideoloftDemoUid(uid) ||
            organisations?.some(({ id, cameras, coowner }) => cameras && (id === uid || coowner)),
        whiteLabelCondition: ({ appName, isTakeawayVendor, isManything }) =>
            AUDIT_LOGS_ALLOWED_APP_NAMES.includes(appName?.toLowerCase()) &&
            !isTakeawayVendor && !isManything,
        // Shows in main nav normally, but included in more menu in mobile app
        sidebarNav: !isMobileApp,
        sidebarNavSecondary: isMobileApp,
        icon: AuditIcon,
        translationKey: translationKeys.navigation.AUDIT_LOGS,
        demo: true,
        mobileApp: true,
        beta: false
    },
    takeawayOrders: {
        path: '/orders',
        sidebarNav: true,
        navbar: true,
        layout: LAYOUT_APP,
        restrictAccess: ['u'],
        icon: OrdersIcon,
        translationKey: translationKeys.takeaways.ORDERS,
        mobileApp: true,
        renderComponent: ({
            organisations,
        }) => (
            <TakeawayOrders
                kitchens={organisations}
            />
        ),
        whiteLabelCondition: ({ isTakeawayVendor }) => isTakeawayVendor,
    },
    takeawayDashboard: {
        path: '/dashboard',
        sidebarNav: true,
        navbar: true,
        layout: LAYOUT_APP,
        restrictAccess: ['u'],
        icon: GraphIcon,
        translationKey: translationKeys.navigation.DASHBOARD,
        renderComponent: ({
            organisations,
        }) => (
            <TakeawayDashboard
                kitchens={organisations}
            />
        ),
        whiteLabelCondition: ({ isTakeawayVendor }) => isTakeawayVendor,
    },
    takeawaySettings: {
        path: '/settings',
        sidebarNav: true,
        layout: LAYOUT_APP,
        restrictAccess: ['u'],
        icon: SettingsIcon,
        translationKey: translationKeys.technical.settings.SETTINGS,
        renderComponent: ({ organisations }) => (
            <TakeawaySettings
                kitchens={organisations}
            />
        ),
        whiteLabelCondition: ({ isTakeawayVendor }) => isTakeawayVendor
    },
    whatsNew: {
        translationKey: translationKeys.navigation.WHATS_NEW,
        path: '/product-updates',
        sidebarNavSecondary: true,
        icon: WhatsNewIcon,
        renderComponent: () => () => {
            // Use replace as path /product-updates doesn't actually exist within this app
            if (isMobileApp) {
                window.open('https://videoloft.com/product-updates?app=true', '_blank');
                return null;
            } else {
                window.location.replace('https://videoloft.com/product-updates');
                return null;
            }
        },
        layout: LAYOUT_NONE,
        whiteLabelCondition: ({ isVideoloft, isTakeawayVendor }) => isVideoloft && !isTakeawayVendor,
        mobileApp: true
    },
    changeIcon: {
        translationKey: 'Change Icon', // Will be overridden dynamically
        path: '/changeIcon',
        sidebarNavSecondary: true,
        icon: EditIcon,
        renderComponent: () => () => {
            if (isMobileApp) {
                window.open('/changeIcon?app=true', '_blank');
                return null;
            } else {
                window.location.replace('/changeIcon');
                return null;
            }
        },
        layout: LAYOUT_NONE,
        mobileApp: true
    },
    resetIcon: {
        translationKey: 'Reset Icon', // Will be overridden dynamically
        path: '/resetIcon',
        sidebarNavSecondary: true,
        icon: ResetIcon,
        renderComponent: () => () => {
            if (isMobileApp) {
                window.open('/resetIcon?app=true', '_blank');
                return null;
            } else {
                window.location.replace('/resetIcon');
                return null;
            }
        },
        layout: LAYOUT_NONE,
        mobileApp: true
    },
    partnerPortal: {
        path: '/partner-portal',
        startPath: PARTNER_PORTAL_START_PATH,
        translationKey: translationKeys.navigation.PARTNER_PORTAL,
        tooltipDescriptionKey: translationKeys.navigation.sidebar.tooltips.PARTNER_PORTAL,
        layout: LAYOUT_APP,
        sidebarNavSecondary: true,
        icon: ManageIcon,
        restrictAccess: ['i', 'd', 's'],
        renderComponent: () => <PartnerPortal />,
        routes: {
            gettingStarted: { path: '/getting-started' },
            partnerDetails: { path: '/partner-details' },
            customers: { path: '/customers' },
            billing: { path: '/billing' },
            materials: { path: '/materials' },
            pricing: { path: '/pricing' },
            cloudAdapters: { path: '/cloud-adapters' }
        },
        whiteLabelCondition: ({ isTakeawayVendor, isManything }) => !isTakeawayVendor && !isManything
    },
    account: {
        path: '/account',
        translationKey: translationKeys.account.ACCOUNT,
        layout: {
            type: LAYOUT_APP,
        },
        restrictAccess: ['u', 'i', 'd', 's'],
        icon: AccountIcon,
        // Shows in secondary nav normally, but included in bottom bar in mobile app
        sidebarNav: isMobileApp,
        sidebarNavSecondary: !isMobileApp,
        routes: {
            details: { path: '/details' },
            planBilling: { path: '/plan-billing' },
            additionalUsers: { path: '/additional-users' },
            videoloftOptIns: { path: '/videoloft-opt-ins' },
        },
        renderComponent: (
            { email, ssoProvider, isSelfReg, ...accountInfo },
            { handleChangeEmail, handleLogin, handleLogout, handleNetworkError }
        ) => (
            <Account
                email={email}
                updateEmail={handleChangeEmail}
                updateLoginInfo={handleLogin}
                onInvalidToken={handleLogout}
                onNetworkError={handleNetworkError}
                ssoProvider={ssoProvider}
                isSelfReg={isSelfReg}
                accountInfo={{ email, ssoProvider, isSelfReg, ...accountInfo }}
            />
        ),
        mobileApp: true
    },
    oauthStart: {
        path: '/oauth/:provider/start',
        layout: LAYOUT_NONE,
        renderComponent: () => <OAuthStart type="oauth" />,
        whiteLabelCondition: ({ isVideoloft }) => isVideoloft
    },
    oauthEnd: {
        path: '/oauth/:provider/end',
        layout: LAYOUT_FULLSCREEN,
        renderComponent: () => (
            <OAuthEnd
                type="oauth"
            />
        ),
        whiteLabelCondition: ({ isVideoloft }) => isVideoloft
    },
    ssoStart: {
        path: '/sso/:provider/start',
        layout: LAYOUT_NONE,
        renderComponent: () => <OAuthStart type="sso" />,
        whiteLabelCondition: ({ supportsSSO }) => supportsSSO,
        mobileApp: true
    },
    ssoEnd: {
        path: '/sso/:provider/end',
        layout: LAYOUT_FULLSCREEN,
        renderComponent: (accountData, { handleLogin, handleLogout, handleNetworkError }) => (
            <OAuthEnd
                type="sso"
                handleLogin={handleLogin}
                onInvalidToken={handleLogout}
                onNetworkError={handleNetworkError}
            />
        ),
        whiteLabelCondition: ({ supportsSSO }) => supportsSSO,
        redirectIfLoggedIn: true,
        mobileApp: true
    },
    ssoConvertEnd: {
        path: '/sso/:provider/convert/end',
        layout: LAYOUT_FULLSCREEN,
        renderComponent: (accountData, { handleLogin, handleLogout, handleNetworkError }) => (
            <OAuthEnd
                type="sso-convert"
                handleLogin={handleLogin}
                onInvalidToken={handleLogout}
                onNetworkError={handleNetworkError}
            />
        ),
        whiteLabelCondition: ({ supportsSSO }) => supportsSSO,
        mobileApp: true
    },
    cameraGroups: {
        path: '/camera-groups',
        layout: {
            type: LAYOUT_APP
        },
        restrictAccess: ['u', 'i', 'd', 's'],
        renderComponent: ({ organisations, cameraGroups }, { handleNewCameraGroup, handleEditCameraGroup, handleDeleteCameraGroup }) => (
            <CreateEditCameraGroup
                organisations={organisations}
                cameraGroups={cameraGroups}
                addCameraGroup={handleNewCameraGroup}
                deleteCameraGroup={handleDeleteCameraGroup}
                editCameraGroup={handleEditCameraGroup}
            />
        ),
        routes: {
            createCameraGroup: {
                path: '/new'
            },
            editCameraGroup: {
                path: '/:id'
            }
        },
        // Page only available if user has access to at least one camera
        accountCondition: ({ organisations }) => organisations?.find(({ cameras }) => cameras),
        demo: true
    },
    install: {
        path: '/install',
        navbar: true,
        // sidebarNav: true,
        icon: () => <InstallIcon style={{width: 22, height: 22}}/>,
        translationKey: translationKeys.install.INSTALL,
        layout: {
            type: LAYOUT_APP,
            fullWidth: true,
        },
        restrictAccess: ['u', 'i', 's'],
        renderComponent: (
            accountData,
            { handleChangeOrganisation, logoutAction }
        ) => (
            <Install
                changeUserCamerasData={handleChangeOrganisation}
                onLogoutClick={logoutAction}
                accountData={accountData}
            />
        ),
        accountCondition: ({ installAllowed, organisations }) => installAllowed || organisations?.some(o => o.coowner),
        whiteLabelCondition: ({ isTakeawayVendor, isManything }) => !isTakeawayVendor && !isManything,
        mobileApp: true
    },
    terms: {
        path: '/terms',
        layout: LAYOUT_NONE,
        demo: true,
        renderComponent: () => (
            <Terms />
        ),
        mobileApp: true
    },
    fullWhiteLabelTerms: {
        path: '/terms-of-service',
        layout: LAYOUT_HEADER_FOOTER,
        demo: true,
        whiteLabelCondition: ({ termsPrivacyData }) => termsPrivacyData?.type === 'full-white-label',
        renderComponent: () => (
            <FullWhiteLabelTerms />
        ),
        mobileApp: true
    },
    privacy: {
        path: '/privacy',
        layout: LAYOUT_NONE,
        demo: true,
        renderComponent: () => (
            <Privacy />
        ),
        mobileApp: true
    },
    fullWhiteLabelPrivacy: {
        path: '/privacy-policy',
        layout: LAYOUT_HEADER_FOOTER,
        demo: true,
        whiteLabelCondition: ({ termsPrivacyData }) => termsPrivacyData?.type === 'full-white-label',
        renderComponent: () => (
            <FullWhiteLabelPrivacy />
        ),
        mobileApp: true
    },
};

// If this is the mobile app, delete excluded routes
if (isMobileApp) {
    _.keys(routes).forEach(page => {
        if (!routes[page].mobileApp) {
            delete routes[page];
        }
    });
}

// Get all paths belonging to a page, including its path, other accepted paths, and all subroutes
const getAllPaths = ({ path, acceptPaths, routes }) => {
    const paths = [path];

    if (acceptPaths) {
        paths.push(...acceptPaths);
    }

    if (routes) {
        for (const page in routes) {
            paths.push(...getAllPaths(routes[page]));
        }
    }

    return paths;
};

// Build up subroute paths, e.g. subroute /additional-users under page /account becomes '/account/additional-users'
// Then add allPaths property to each page's config which is array of all paths that go that page (including subroutes)
const buildPaths = (pageConfig, currentPath) => {
    pageConfig.path = currentPath + pageConfig.path;

    if (pageConfig.routes) {
        for (const page in pageConfig.routes) {
            buildPaths(pageConfig.routes[page], pageConfig.path);
        }
    }

    pageConfig.allPaths = getAllPaths(pageConfig);
};

for (const page in routes) {
    buildPaths(routes[page], '');
}

export default routes;

// Helpers

export const getRoutesWithLayout = (layout) => {
    if (layout === LAYOUT_HEADER_FOOTER) {
        // Treat default layout differently as it may not be specified in route's config
        return _.filter(
            routes,
            ({ layout }) =>
                !(typeof layout === 'object' ? layout?.type : layout) ||
                (layout?.type ?? layout) === LAYOUT_HEADER_FOOTER
        );
    } else {
        return _.filter(
            routes,
            ({ layout: routeLayout }) =>
                (routeLayout?.type ?? routeLayout) === layout
        );
    }
};

// Only returns first layer object (so not specific subroute objects - though they will be included in the `routes` property of the returned object)
export const getRouteByPath = (path) => {
    return _.find(routes, ({ allPaths }) => allPaths.includes(path));
};

// Replaces URL params in path with specified values
export const getPathWithParams = (path, values) => {
    _.forEach(values, (value, param) => {
        path = path.replace(`:${param}`, value);
    });
    return path;
};

// Returns true if user can access page, false otherwise
// Pass two object arguments: route config and account info
// Needs to check both restrictAccess and accountCondition
export const canUserAccessRoute = ({ restrictAccess, accountCondition }, accountInfo) => (!restrictAccess || restrictAccess.includes(accountInfo.accountType)) && (!accountCondition || accountCondition(accountInfo));
